/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable eqeqeq */

import React, { useEffect, useState, useMemo } from "react";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { FaUniversity } from "react-icons/fa";
import Select from "react-select";
import { crudDe } from "../../../../../services/acesso";
import api from "../../../../../services/api";
import Listagem from "../../../../../components/listagem";
import ModalForm from "../../../../../components/modal-form";
import { useAppSelector } from "../../../../../store/hooks";
import { TipoFormulario } from "../../../../../services/constantes";
import {
    ApiError,
    Banco,
    BancoFebraban,
    Campo,
    Opcao,
    TipoChavePix,
    TipoContaBancaria,
} from "../../../../../services/tipos";
import { LocaSmartLayout } from "../../../../../components/locasmart-layout";
import { obterIdTenent, usuarioTemPermissao } from "../../../../../services/seguranca";
import { useFiliais } from "../../../../../hooks/useFiliais";

const opcoesTipoConta: Opcao[] = [
    { label: "Corrente", valor: TipoContaBancaria.Corrente },
    { label: "Poupança", valor: TipoContaBancaria.Poupanca },
    { label: "Pagamento", valor: TipoContaBancaria.Pagamento },
];

const opcoesChavePix: Opcao[] = [
    { label: "CPF", valor: TipoChavePix.CPF },
    { label: "CNPJ", valor: TipoChavePix.CNPJ },
    { label: "E-mail", valor: TipoChavePix.Email },
    { label: "Telefone", valor: TipoChavePix.Telefone },
    { label: "Aleatória", valor: TipoChavePix.Aleatoria },
];

type FebrabanOption = { value: string; label: string; banco: BancoFebraban };

interface BuscaFebrabanProps {
    value: any;
    onChange: (val: any) => void;
    campo: Campo;
    entidade?: any;
    handleInputChange?: (campo: string, valor: string | number | boolean) => void;
}

/**
 * Campo custom que encapsula a busca de banco via BrasilAPI.
 * Quando um banco é selecionado, preenche automaticamente codigoFebraban e nomeBanco
 * na entidade através do handleInputChange fornecido pelo ModalForm.
 */
function BuscaFebrabanField({ value, onChange, entidade, handleInputChange }: BuscaFebrabanProps) {
    const [opcoes, setOpcoes] = useState<FebrabanOption[]>([]);
    const [carregando, setCarregando] = useState(false);

    useEffect(() => {
        let ativo = true;
        setCarregando(true);
        api.get("/banco/febraban")
            .then(r => {
                if (!ativo) return;
                const lista: BancoFebraban[] = r.data || [];
                setOpcoes(
                    lista
                        .filter(b => b.code != null)
                        .map(b => ({
                            value: String(b.code),
                            label: `${b.code} - ${b.fullName || b.name || ""}`,
                            banco: b,
                        }))
                );
            })
            .catch(() => {
                if (ativo) setOpcoes([]);
            })
            .finally(() => {
                if (ativo) setCarregando(false);
            });
        return () => { ativo = false; };
    }, []);

    const selecionado = useMemo(
        () => opcoes.find(o => o.value === String(entidade?.codigoFebraban ?? "")) || null,
        [opcoes, entidade?.codigoFebraban]
    );

    return (
        <div>
            <label style={{ fontWeight: 500, fontSize: "0.9rem" }}>
                Buscar banco pelo código/nome
            </label>
            <Select<FebrabanOption>
                isClearable
                isLoading={carregando}
                options={opcoes}
                value={selecionado}
                placeholder="Digite o código ou nome do banco…"
                noOptionsMessage={() => "Nenhum banco encontrado"}
                onChange={opt => {
                    if (opt) {
                        handleInputChange?.("codigoFebraban", opt.banco.code ? String(opt.banco.code) : "");
                        handleInputChange?.("nomeBanco", opt.banco.fullName || opt.banco.name || "");
                    } else {
                        handleInputChange?.("codigoFebraban", "");
                        handleInputChange?.("nomeBanco", "");
                    }
                    onChange(opt?.value ?? "");
                }}
            />
            <small className="text-muted">
                Selecione o banco para preencher automaticamente o código e o nome.
            </small>
        </div>
    );
}

function BancoListagem() {
    const { empresaTemFiliais, filiaisOptions, filialSelecionada, getCampoFilial, getAcaoEmLoteFilial } = useFiliais();
    const [bancos, setBancos] = useState<Banco[]>([]);
    const [fezAlgumaAcao, setFezAlgumaAcao] = useState(false);
    const [carregando, setCarregando] = useState<boolean>(false);
    const [exibirFormModal, setExibirFormModal] = useState(false);
    const [token] = useState(useAppSelector(state => state.token));
    const [realizarConsulta, setRealizarConsulta] = useState<boolean>(true);
    const [modo, setModo] = useState<TipoFormulario>(TipoFormulario.tpNenhum);
    const [bancoSelecionado, setBancoSelecionado] = useState<Banco>({} as Banco);

    const temPermissao = usuarioTemPermissao(token, 'banco_visualizar');

    const linhasBanco: any[] = useMemo(
        () => bancos.map(b => {
            const agenciaStr = b.agencia
                ? `${b.agencia}${b.digitoAgencia ? "-" + b.digitoAgencia : ""}`
                : "";
            const contaStr = b.conta
                ? `${b.conta}${b.digitoConta ? "-" + b.digitoConta : ""}`
                : "";
            let contaResumo = "";
            if (agenciaStr && contaStr) contaResumo = `${agenciaStr} / ${contaStr}`;
            else if (agenciaStr) contaResumo = agenciaStr;
            else if (contaStr) contaResumo = contaStr;
            return {
                ...b,
                tipoContaLabel: opcoesTipoConta.find(o => o.valor === b.tipoConta)?.label ?? "",
                contaResumo,
                ativoLabel: b.ativo ? "Ativo" : "Inativo",
            };
        }),
        [bancos]
    );

    const colunas = [
        { field: 'apelido', headerName: 'Apelido', flex: 1 },
        { field: 'nomeBanco', headerName: 'Banco', flex: 1 },
        { field: 'codigoFebraban', headerName: 'Código', flex: 0.5 },
        { field: 'contaResumo', headerName: 'Agência / Conta', flex: 1 },
        { field: 'tipoContaLabel', headerName: 'Tipo de Conta', flex: 0.7 },
        { field: 'ativoLabel', headerName: 'Status', flex: 0.5 },
        ...(empresaTemFiliais ? [{
            field: 'idFilial', headerName: 'Unidade', flex: 1,
            renderCell: (params: any) => {
                if (!params.value) return '';
                const filial = filiaisOptions.find((f: any) => f.valor === params.value);
                return filial?.label || '';
            }
        }] : []),
    ];

    const campoFilial = getCampoFilial({ linha: 0, coluna: 1, largura: 6 });

    const campos: Campo[] = [
        ...(campoFilial ? [campoFilial] : []),
        // Aviso destacado
        {
            label: "", nome: "_infoBanco", tipo: "aviso", required: false,
            largura: 12, coluna: 1, linha: 1, visible: true,
            alertType: "info",
            alertMessage: "Esta seleção é apenas para conciliação manual — não movimenta dinheiro automaticamente.",
        },

        // Toggle "Não encontrei meu banco"
        {
            label: "Não encontrei meu banco (preencher manualmente)",
            nome: "_naoEncontreiBanco", tipo: "checkbox", required: false,
            largura: 12, coluna: 1, linha: 2, visible: true,
            ajudinha: {
                texto: "Marque para desabilitar a busca automática e preencher código e nome manualmente.",
                autoCloseTime: 3,
            },
        },

        // Busca FEBRABAN (custom) — visível quando NÃO marcou "não encontrei"
        {
            label: "", nome: "_bancoFebrabanSearch", tipo: "custom", required: false,
            largura: 12, coluna: 1, linha: 3,
            visible: (e: any) => !e._naoEncontreiBanco,
            component: (props) => <BuscaFebrabanField {...props} />,
        },

        // Código FEBRABAN — readonly quando autocomplete ativo, editável quando manual
        {
            label: "Código FEBRABAN", nome: "codigoFebraban", tipo: "text", required: false,
            largura: 4, coluna: 1, linha: 4,
            visible: (e: any) => !e._naoEncontreiBanco,
            apenasLeitura: true,
        },
        {
            label: "Código FEBRABAN", nome: "codigoFebraban", tipo: "text", required: false,
            largura: 4, coluna: 1, linha: 4,
            visible: (e: any) => !!e._naoEncontreiBanco,
        },

        // Nome do banco — mesmo padrão (duas entradas, visibilidade mutuamente exclusiva)
        {
            label: "Nome do Banco", nome: "nomeBanco", tipo: "text", required: false,
            largura: 8, coluna: 2, linha: 4,
            visible: (e: any) => !e._naoEncontreiBanco,
            apenasLeitura: true,
        },
        {
            label: "Nome do Banco", nome: "nomeBanco", tipo: "text", required: false,
            largura: 8, coluna: 2, linha: 4,
            visible: (e: any) => !!e._naoEncontreiBanco,
        },

        // Apelido + Tipo de Conta
        {
            label: "Apelido", nome: "apelido", tipo: "text", required: true,
            largura: 6, coluna: 1, linha: 5, visible: true,
            ajudinha: {
                texto: "Nome que aparecerá nas telas de pagamento e relatórios.",
                autoCloseTime: 3,
            },
        },
        {
            label: "Tipo de Conta", nome: "tipoConta", tipo: "select", required: false,
            largura: 6, coluna: 2, linha: 5, visible: true,
            opcoes: opcoesTipoConta,
        },

        // Agência + Dígito / Conta + Dígito
        {
            label: "Agência", nome: "agencia", tipo: "text", required: false,
            largura: 4, coluna: 1, linha: 6, visible: true,
        },
        {
            label: "Dígito", nome: "digitoAgencia", tipo: "text", required: false,
            largura: 2, coluna: 2, linha: 6, visible: true,
            maximoCaracteres: 5,
        },
        {
            label: "Conta", nome: "conta", tipo: "text", required: false,
            largura: 4, coluna: 3, linha: 6, visible: true,
        },
        {
            label: "Dígito", nome: "digitoConta", tipo: "text", required: false,
            largura: 2, coluna: 4, linha: 6, visible: true,
            maximoCaracteres: 5,
        },

        // Titular + Documento
        {
            label: "Titular", nome: "titular", tipo: "text", required: false,
            largura: 8, coluna: 1, linha: 7, visible: true,
        },
        {
            label: "CPF/CNPJ do Titular", nome: "documentoTitular", tipo: "text", required: false,
            largura: 4, coluna: 2, linha: 7, visible: true,
        },

        // PIX
        {
            label: "Tipo Chave PIX", nome: "tipoChavePix", tipo: "select", required: false,
            largura: 4, coluna: 1, linha: 8, visible: true,
            opcoes: opcoesChavePix,
            selectBoxIsCleareable: true,
        },
        {
            label: "Chave PIX", nome: "chavePix", tipo: "text", required: false,
            largura: 8, coluna: 2, linha: 8, visible: true,
        },

        // Observações
        {
            label: "Observações", nome: "observacoes", tipo: "memo", required: false,
            largura: 12, coluna: 1, linha: 9, visible: true,
        },

        // Ativo — só em edição
        {
            label: "Ativo", nome: "ativo", tipo: "checkbox", required: false,
            largura: 4, coluna: 1, linha: 10,
            visible: () => modo === TipoFormulario.tpEdicao,
        },

        // Id hidden
        {
            label: "Id", nome: "id", tipo: "text", required: false,
            largura: 12, coluna: 1, linha: 11, visible: false,
        },
    ];

    function tratarErro(error: AxiosError<ApiError>) {
        setCarregando(false);
        const msg = error.response?.data?.Mensagem ?? "Erro desconhecido";
        toast.error("Não foi possível continuar a operação banco: " + msg);
    }

    const requisicaoBemSucedida = (_response: any) => {
        setRealizarConsulta(true);
        setExibirFormModal(false);
        toast.success("Banco salvo com sucesso");
        setFezAlgumaAcao(true);
    };

    const handleFecharModal = () => {
        setExibirFormModal(false);
        if ((modo == TipoFormulario.tpEdicao || modo == TipoFormulario.tpCadastro) && fezAlgumaAcao) {
            setRealizarConsulta(true);
            setFezAlgumaAcao(false);
        }
    };

    const handleCadastrar = () => {
        if (usuarioTemPermissao(token, 'banco_novo')) {
            setModo(TipoFormulario.tpCadastro);
            setBancoSelecionado({
                ativo: true,
                tipoConta: TipoContaBancaria.Corrente,
                ...(filialSelecionada ? { idFilial: filialSelecionada } : {}),
            } as Banco);
            setExibirFormModal(true);
        } else {
            toast.error("Você não tem permissão para cadastrar um novo banco");
        }
    };

    const handleEditar = (banco: any) => {
        if (usuarioTemPermissao(token, 'banco_editar')) {
            setModo(TipoFormulario.tpEdicao);
            setBancoSelecionado(banco);
            setExibirFormModal(true);
        } else {
            toast.error("Você não tem permissão para editar um banco");
        }
    };

    const handleExclusao = async (banco: Banco) => {
        if (usuarioTemPermissao(token, 'banco_excluir')) {
            setCarregando(true);
            await api.delete(`/banco/${banco.id}`)
                .then(() => {
                    setTimeout(() => {
                        toast.success("Banco excluído com sucesso");
                        setRealizarConsulta(true);
                    }, 250);
                })
                .catch((error: AxiosError<ApiError>) => {
                    setCarregando(false);
                    const msg = error.response?.data?.Mensagem ?? "Banco em uso. Inative ao invés de excluir.";
                    toast.error(msg);
                });
        }
    };

    const handleVisualizacao = (banco: any) => {
        if (temPermissao) {
            setBancoSelecionado(banco);
            setExibirFormModal(true);
            setModo(TipoFormulario.tpVisualizacao);
        }
    };

    useEffect(() => {
        const fetchBancos = async () => {
            if (temPermissao && realizarConsulta) {
                setCarregando(true);
                await api.get(`/bancoPorEmpresa/${obterIdTenent(token)}?apenasAtivos=false`)
                    .then(response => {
                        setTimeout(() => {
                            setBancos(response.data);
                            setRealizarConsulta(false);
                            setCarregando(false);
                        }, 250);
                    })
                    .catch((error: AxiosError<ApiError>) => {
                        setCarregando(false);
                        tratarErro(error);
                    });
            }
        };
        fetchBancos();
    }, [temPermissao, realizarConsulta, token]);

    return (
        <>
            <LocaSmartLayout>
                <Listagem
                    permissoes={crudDe('banco')}
                    colunas={colunas}
                    linhas={linhasBanco}
                    nomeEntidade="Banco"
                    nomePropriedade="apelido"
                    carregando={carregando}
                    IconeEntidade={FaUniversity}
                    exibirAcoesDeCrud={true}
                    handleEditar={handleEditar}
                    handleCriar={handleCadastrar}
                    handleExclusao={handleExclusao}
                    handleVisualizacao={handleVisualizacao}
                    handleSelecionarEntidade={setBancoSelecionado as any}
                    acoesEmLote={[
                        ...(getAcaoEmLoteFilial('Banco', () => setRealizarConsulta(true)) ? [getAcaoEmLoteFilial('Banco', () => setRealizarConsulta(true))!] : []),
                    ]}
                />

                <ModalForm
                    modo={modo}
                    tamanho="lg"
                    campos={campos}
                    show={exibirFormModal}
                    carregando={carregando}
                    onHide={handleFecharModal}
                    onCancel={handleFecharModal}
                    ajudinha={{
                        texto: "Cadastre as contas bancárias da sua empresa para identificar em quais contas os valores são recebidos.",
                        autoCloseTime: 4,
                        ehDestacado: true,
                    }}
                    entidadeSelecionada={bancoSelecionado}
                    apiConfig={{
                        endpoint: "banco",
                        requisicaoBemSucedida,
                        tratarErro,
                        idTenentCampo: "idEmpresa",
                    }}
                />
            </LocaSmartLayout>
        </>
    );
}

export default BancoListagem;
